import * as api from '@gitbook/api-client';

import {
    findRequiredPlanForFeature,
    getBlockedFeature,
    getFeatureDefinition,
    hasAccessToFeature,
    type PricingFeature,
} from './table_features';
import type { PricingProductDefinition } from './table_products';
import { compareSiteTypes } from './table_sites';
import {
    getMinimumSiteSubscriptionForFeatures,
    isSiteFeatureIncludedInPlan,
    SITE_FEATURES_MAP,
} from './table_sites_features';

/** The organization fields that decide access to a feature. */
type FeatureAccessOrganization = Pick<api.Organization, 'plan' | 'ai' | 'aiSettings' | 'aiPaused'>;

/** A feature to check, with the site for a site feature. */
export type FeatureAccess =
    | {
          featureKey: Exclude<api.OrganizationFeature, api.OrganizationFeature.MaxMembers>;
          site?: never;
      }
    | {
          featureKey: api.SiteFeatureKey;
          /** The site's applied plan takes precedence over its own. */
          site: { type: api.SiteType; appliedType?: api.SiteType | null };
      };

/** A site to check. When its enabled features are known, the plan they came from is reused. */
export type FeatureUpgradeSite = {
    type: api.SiteType;
    /** The site's applied plan takes precedence over its own. */
    appliedType?: api.SiteType | null;
    features?: api.SiteFeature[];
};

export type FeatureUpgradeInput = {
    organization: FeatureAccessOrganization & Pick<api.Organization, 'billing'>;
} & (
    | { featureKey: api.OrganizationFeature; site?: never }
    | { featureKey: api.SiteFeatureKey; site: FeatureUpgradeSite }
);

export type FeatureUpgrade =
    | {
          type: 'organization';
          featureKey: api.OrganizationFeature;
          plan: PricingProductDefinition | undefined;
      }
    | { type: 'site'; featureKey: api.SiteFeatureKey; plan: api.SiteType }
    | { type: 'contact-support'; featureKey: api.SiteFeatureKey }
    /** The plans cover the feature, but its AI preference or the master setting is off. */
    | { type: 'ai-disabled'; featureKey: api.OrganizationFeature.Ai }
    /** AI can't be enabled for the organization at all. */
    | { type: 'ai-restricted'; featureKey: api.OrganizationFeature.Ai }
    | { type: 'ai-paused'; featureKey: api.OrganizationFeature.Ai };

/**
 * Check a feature's entitlement and all dependencies, including AI settings and automatic pausing.
 */
export function isPricingFeatureAvailable(
    organization: FeatureAccessOrganization,
    feature: FeatureAccess
): boolean {
    const hasEntitlement = feature.site
        ? isSiteFeatureIncludedInPlan(
              feature.featureKey,
              feature.site.appliedType ?? feature.site.type
          )
        : hasOrganizationFeatureAccess(organization, feature.featureKey);
    if (!hasEntitlement || getAIRestriction(organization, feature.featureKey)) {
        return false;
    }

    return getOrganizationFeatureDependencies(organization.plan, feature.featureKey).every(
        (featureKey) => hasOrganizationFeatureAccess(organization, featureKey)
    );
}

/** Find an organization upgrade requirement from the feature and its dependencies, using only the plan. */
export function getBlockedOrganizationFeature(
    plan: api.BillingProduct,
    featureKey: PricingFeature
): api.OrganizationFeature | undefined {
    const dependencies = getOrganizationFeatureDependencies(plan, featureKey);
    return getBlockedFeature(
        isSiteFeatureKey(featureKey) ? dependencies : [featureKey, ...dependencies],
        plan
    );
}

/** Return transitive organization prerequisites in order, for access checks and upgrade prompts. */
export function getOrganizationFeatureDependencies(
    plan: api.BillingProduct,
    featureKey: PricingFeature
): Exclude<api.OrganizationFeature, api.OrganizationFeature.MaxMembers>[] {
    const dependencies = new Set<
        Exclude<api.OrganizationFeature, api.OrganizationFeature.MaxMembers>
    >();
    const visiting = new Set<api.OrganizationFeature | api.SiteFeatureKey>();

    function visit(current: PricingFeature) {
        if (visiting.has(current)) {
            throw new Error(`Circular feature dependency: ${current}`);
        }
        visiting.add(current);
        const definition = isSiteFeatureKey(current)
            ? SITE_FEATURES_MAP[current]
            : getFeatureDefinition(current);

        for (const dependencyKey of definition?.dependsOn ?? []) {
            if (visiting.has(dependencyKey) || !dependencies.has(dependencyKey)) {
                dependencies.add(dependencyKey);
                visit(dependencyKey);
            }
        }
        visiting.delete(current);
    }

    visit(featureKey);
    return [...dependencies];
}

/**
 * Resolve what keeps a feature locked: an organization plan, a site plan, or the organization's
 * AI settings. Plans come first, since enabling AI alone would not unlock the feature.
 */
export function getUpgradeRequirement(input: FeatureUpgradeInput): FeatureUpgrade | undefined {
    const blockedFeature = getBlockedOrganizationFeature(input.organization.plan, input.featureKey);
    if (blockedFeature) {
        return {
            type: 'organization',
            featureKey: blockedFeature,
            plan: findRequiredPlanForFeature(blockedFeature),
        };
    }
    if (input.site) {
        const { site, featureKey } = input;
        const currentSiteType = site.appliedType ?? site.type;
        const requiredSiteType = getRequiredSiteTypeForFeature({ site, featureKey });
        if (!requiredSiteType) {
            return { type: 'contact-support', featureKey };
        }
        if (compareSiteTypes(requiredSiteType, currentSiteType) > 0) {
            return { type: 'site', featureKey, plan: requiredSiteType };
        }
    }
    const aiRestriction = getAIRestriction(input.organization, input.featureKey);
    if (aiRestriction) {
        return { type: aiRestriction, featureKey: api.OrganizationFeature.Ai };
    }
    return undefined;
}

/**
 * Return the minimum site plan needed for a specific site to access a feature.
 */
export function getRequiredSiteTypeForFeature({
    site,
    featureKey,
}: {
    site: FeatureUpgradeSite;
    featureKey: api.SiteFeatureKey;
}): api.SiteType | null {
    const currentSiteType = site.appliedType ?? site.type;
    const existingFeature = site.features?.find((feature) => feature.id === featureKey);
    if (isSiteFeatureIncludedInPlan(featureKey, currentSiteType)) {
        return currentSiteType;
    }

    if (existingFeature) {
        return existingFeature.plan;
    }

    return getMinimumSiteSubscriptionForFeatures({
        featureKeys: [featureKey],
        defaultSiteType: currentSiteType,
    });
}

/** Identify AI settings that block a feature independently of its plan entitlement. */
export function getAIRestriction(
    organization: FeatureAccessOrganization & Pick<api.Organization, 'billing'>,
    featureKey: PricingFeature
): 'ai-restricted' | 'ai-disabled' | 'ai-paused' | undefined {
    const features = [
        featureKey,
        ...getOrganizationFeatureDependencies(organization.plan, featureKey),
    ];
    if (organization.billing?.canUseAI === false && features.includes(api.OrganizationFeature.Ai)) {
        return 'ai-restricted';
    }
    if (!organization.ai && features.includes(api.OrganizationFeature.Ai)) {
        return 'ai-disabled';
    }
    const featureDisabled = features.some((key) => {
        const definition = isSiteFeatureKey(key)
            ? SITE_FEATURES_MAP[key]
            : getFeatureDefinition(key);
        return (
            definition?.aiFeature !== undefined &&
            organization.aiSettings?.[definition.aiFeature] === false
        );
    });
    if (featureDisabled) {
        return 'ai-disabled';
    }
    if (organization.aiPaused && features.includes(api.OrganizationFeature.Ai)) {
        return 'ai-paused';
    }
    return undefined;
}

function isSiteFeatureKey(featureKey: PricingFeature): featureKey is api.SiteFeatureKey {
    return Object.hasOwn(SITE_FEATURES_MAP, featureKey);
}

function hasOrganizationFeatureAccess(
    organization: FeatureAccessOrganization,
    featureKey: Exclude<api.OrganizationFeature, api.OrganizationFeature.MaxMembers>
): boolean {
    return (
        (featureKey !== api.OrganizationFeature.Ai || Boolean(organization.ai)) &&
        hasAccessToFeature(organization.plan, featureKey)
    );
}
